import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { SiteList, SiteSummary } from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { CursorListView } from "../../components/CursorListView";
import { Button, ButtonLink, Card, EmptyState, Input, PageHeader, StatusPill, Table } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { useCursorList } from "../../lib/useCursorList";
import { useIntentKey } from "../../lib/useIntentKey";
import { SITE_STATUS } from "./siteCopy";

// UX-DESIGN.md §3.1. GET /v1/sites (G-32, publisher_reads.go:180) lists
// every site with its effective status (a suspension shows as
// suspended_*, publisher_reads.go:239-248). POST /v1/sites {domain} is
// idempotent since 0057 and returns the new site with its DNS challenge.

function domainError(v: string): string | null {
  const d = v.trim().toLowerCase();
  if (!d) return "Enter your site's domain.";
  if (d.includes("://") || d.includes("/")) return "Enter just the domain, like mcp.example.com — no https:// or path.";
  if (!/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(d)) return "Enter a valid domain name, like mcp.example.com.";
  return null;
}

export function SitesPage() {
  const ep = useEndpoints();
  const list = useCursorList<SiteList, SiteSummary>(["sites"], (cursor) => ep.publisher.listSites(cursor), (p) => p.sites);

  return (
    <div className="stack-lg">
      <PageHeader
        title="Sites"
        description="Each site is an MCP server you've registered. Prove domain control, add slots, then create API keys for the SDK."
        actions={<ButtonLink to="/publisher/sites/new" variant="primary">Add site</ButtonLink>}
      />
      <Card title="All sites">
        <CursorListView
          list={list}
          loadingLabel="Loading sites"
          empty={
            <EmptyState icon="globe" title="No sites yet" action={<ButtonLink to="/publisher/sites/new">Add a site</ButtonLink>}>
              Register the domain your MCP server is reached at, then prove you control it.
            </EmptyState>
          }
        >
          {(rows) => (
            <Table
              caption="Your sites, newest first"
              captionHidden
              rows={rows}
              rowKey={(s) => s.site_id}
              columns={[
                { key: "domain", header: "Domain", cell: (s) => <Link to={`/publisher/sites/${s.site_id}`}>{s.domain}</Link> },
                {
                  key: "status",
                  header: "Status",
                  cell: (s) => {
                    const st = SITE_STATUS[s.effective_status];
                    return <StatusPill tone={st?.tone ?? "neutral"}>{st?.label ?? s.effective_status}</StatusPill>;
                  },
                },
                { key: "verified", header: "Domain verified", cell: (s) => (s.domain_verified_at ? formatLocal(s.domain_verified_at) : "Not yet") },
                { key: "added", header: "Added", cell: (s) => formatLocal(s.created_at) },
              ]}
            />
          )}
        </CursorListView>
      </Card>
    </div>
  );
}

export function AddSitePage() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const intent = useIntentKey();
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    // One key per domain the user means to add: a retry or double-click
    // replays the first create instead of making a second site (G-21).
    mutationFn: (d: string) => ep.publisher.createSite(d, { idempotencyKey: intent.keyFor(d) }),
    onSuccess: (res) => {
      intent.reset();
      void qc.invalidateQueries({ queryKey: ["sites"] });
      navigate(`/publisher/sites/${res.site_id}?new=1`);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const err = domainError(domain);
    setError(err);
    if (!err) m.mutate(domain.trim().toLowerCase());
  }

  const existing = m.error instanceof ApiError && m.error.code === "site_domain_already_registered" ? m.error.detailString("site_id") : undefined;
  return (
    <div className="stack-lg">
      <PageHeader title="Add a site" back={{ to: "/publisher/sites", label: "Sites" }} />
      <Card>
        <form className="stack" onSubmit={submit} noValidate>
          <div style={{ maxWidth: 480 }}>
            <Input
              label="Domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              error={error}
              hint="The domain your MCP server is reached at. You'll prove you control it with a DNS record next."
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {m.isError ? (
            <CommandError error={m.error}>
              {existing ? (
                <span>
                  <Link to={`/publisher/sites/${existing}`}>Open that site</Link>
                </span>
              ) : null}
            </CommandError>
          ) : null}
          <div>
            <Button type="submit" loading={m.isPending}>
              Add site
            </Button>
          </div>
          <p className="xs muted">
            Declaring your server's tool set and its locale are part of registration review but aren't available yet (API-GAPS.md G-33).
          </p>
        </form>
      </Card>
    </div>
  );
}
